import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CustomerPageLayout from "../customer-page-layout/CustomerPageLayout";
import { EarthIcon } from "../../../assets/icons";
import { CustomerLoader } from "../components";
import { useGetOrderByIdQuery } from "../../../service/customer/order-workflow-service";
import { ProductDetailsSection } from "./components/ProductDetailsSection";
import { OrderInfoSection } from "./components/OrderInfoSection";
import { AttestationPage } from "./components/AttestationPage";
import { AttestationSuccessPage } from "./components/AttestationSuccessPage";
import styles from "./solar-journey.module.scss";

type SolarJourneyView = "order-details" | "attestation" | "attestation-success";
type AttestationResultType = "congratulations" | "feedback";

export const SolarJourneyPage: React.FC = () => {
	const navigate = useNavigate();
	const { orderId } = useParams<{ orderId: string }>();
	const [currentView, setCurrentView] = useState<SolarJourneyView>("order-details");
	const [attestationResult, setAttestationResult] = useState<AttestationResultType>("congratulations");

	const { data, isLoading } = useGetOrderByIdQuery(
		{ orderId: orderId! },
		{ skip: !orderId }
	);

	// Order is attestable only when the last OrderInstallment event is InstallationVerified (typeOfEvent=4)
	// meaning the state machine is in CustomerVerificationInProgress
	const installmentEvents = data?.workflowDetails?.events?.filter(e => e.type === 4) ?? [];
	const lastInstallmentEvent = installmentEvents[installmentEvents.length - 1];
	const isAttestable = lastInstallmentEvent?.typeOfEvent === 4;

	const layoutTitle = currentView === "attestation"
		? "Attestation"
		: currentView === "attestation-success"
			? ""
			: "Your Solar Journey";

	const showBackButton = currentView !== "attestation-success";

	const handleBack = () => {
		if (currentView === "attestation") {
			setCurrentView("order-details");
		} else {
			navigate(-1);
		}
	};

	return (
		<CustomerPageLayout
			title={layoutTitle}
			icon={<EarthIcon />}
			showBackButton={showBackButton}
			onBack={handleBack}
		>
			{isLoading ? (
				<CustomerLoader
					title="Loading Order Details"
					subtitle="Please wait while we fetch your order information..."
				/>
			) : currentView === "attestation" ? (
				<AttestationPage
					orderId={orderId!}
					onCancel={() => setCurrentView("order-details")}
					onSuccess={(type) => {
						setAttestationResult(type);
						setCurrentView("attestation-success");
					}}
				/>
			) : currentView === "attestation-success" ? (
				<AttestationSuccessPage type={attestationResult} />
			) : data ? (
				<div className={styles.pageContainer}>
					<div className={styles.headerRow}>
						<span className={styles.headerTitle}>Order & Installation Status</span>
						{isAttestable && (
							<button
								className={styles.attestButton}
								onClick={() => setCurrentView("attestation")}
							>
								Attest
							</button>
						)}
					</div>
					<div className={styles.contentRow}>
						<div className={styles.leftColumn}>
							<ProductDetailsSection data={data} />
						</div>
						<div className={styles.rightColumn}>
							<OrderInfoSection data={data} />
						</div>
					</div>
				</div>
			) : (
				<div className={styles.pageContainer}>
					<span style={{ color: "var(--app-base-white-05)", textAlign: "center", padding: "2rem" }}>
						Order not found.
					</span>
				</div>
			)}
		</CustomerPageLayout>
	);
};
